import { useEffect, useState } from "react";
import { createActiveClock, USAGE_IDLE_MS } from "./appUsage";

export function useAppUsage({ identity, kind, call }) {
  const [error, setError] = useState("");
  useEffect(() => {
    setError("");
    if (!identity) return undefined;
    const clock = createActiveClock(performance.now(), document.visibilityState === "visible");
    const sessionId = crypto.randomUUID();
    let sequence = 0, pending = null, sending = false, stopped = false, lastInteraction = performance.now();
    const send = async (force = false) => {
      if (sending || stopped) return;
      if (!pending) {
        const activeMs = sequence === 0 ? 0 : clock.take(performance.now());
        if (sequence > 0 && activeMs === 0 && !force) return;
        sequence += 1;
        pending = { kind, sessionId, sequence, activeMs };
      }
      sending = true;
      try {
        await call("appUsageHeartbeat", pending);
        pending = null;
        if (!stopped) setError("");
      } catch (cause) {
        console.error("App usage tracking failed:", cause);
        if (!stopped) setError("Usage tracking is temporarily unavailable; some active time may not be recorded.");
      } finally { sending = false; }
    };
    const interact = event => {
      if (!event.isTrusted) return;
      const time = performance.now();
      const waking = time - lastInteraction >= USAGE_IDLE_MS;
      clock.interact(time);
      lastInteraction = time;
      if (waking) void send(true);
    };
    const visibility = () => {
      const visible = document.visibilityState === "visible";
      clock.visibility(performance.now(), visible);
      void send(visible);
    };
    const events = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];
    events.forEach(name => window.addEventListener(name, interact, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", visibility);
    const timer = setInterval(send, 30000);
    void send();
    return () => {
      stopped = true;
      clearInterval(timer);
      events.forEach(name => window.removeEventListener(name, interact, true));
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [identity, kind, call]);
  return error;
}
